import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import axios from '../utils/axios';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Alert } from '../components/ui/Alert';
import { Modal } from '../components/ui/Modal';
import { LoadingPage } from '../components/feedback/Spinner';
import Layout from '../components/layout/Layout';
import { SubscriptionPaymentForm } from '../components/SubscriptionPaymentForm';

interface SubscriptionPlan {
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  creditsPerMonth: number;
  features: string[];
  description: string;
  cta: string;
  popular?: boolean;
}

const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    name: 'Standard',
    monthlyPrice: 100,
    yearlyPrice: 1080, // 10% discount
    creditsPerMonth: 50,
    description: 'Need simple, reliable measurements?',
    cta: 'Get Started!',
    features: [
      '50 credits per month',
      'Basic measurement tools',
      'Combine multiple trade measurements',
      'Interactive pitch adjustments',
      'Real-time recalculations'
    ]
  },
  {
    name: 'Pro',
    monthlyPrice: 150,
    yearlyPrice: 1620, // 10% discount
    creditsPerMonth: 100,
    description: 'Ready to scale your business?',
    cta: 'BEST DEAL!',
    popular: true,
    features: [
      '100 credits per month',
      'All Standard features',
      'Trade-specific building codes',
      'Solar exposure identity',
      'Permit requirement verification',
      'Priority support'
    ]
  },
  {
    name: 'Premium',
    monthlyPrice: 250,
    yearlyPrice: 2700, // 10% discount
    creditsPerMonth: 200,
    description: 'Want the ultimate solution?',
    cta: 'Go Premium!',
    features: [
      '200 credits per month',
      'All Pro features',
      'Custom line item pricing',
      'Instant Report delivery',
      'White-label reports'
    ]
  }
];

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isYearly, setIsYearly] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);

  const getPrice = (plan: SubscriptionPlan) => {
    return isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  };

  const getMonthlyEquivalent = (plan: SubscriptionPlan) => {
    return isYearly ? (plan.yearlyPrice / 12).toFixed(2) : plan.monthlyPrice;
  };

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
  };

  const handlePaymentSuccess = (data: any) => {
    setSelectedPlan(null);
    alert(`Subscription activated! ${data.credits_added} credits added to your account.`);
    navigate('/dashboard');
  };

  return (
    <Layout>
      <div className="pricing-page">
        <header className="pricing-header">
          <div className="pricing-header-content">
            <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
              <h1 className="pricing-title">Choose Your Plan</h1>
              <p className="pricing-subtitle">Get started with our essential tools for accurate measurements and comprehensive reporting.</p>
            </div>
          </div>
          
          {/* Billing Toggle */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--spacing-4)', marginTop: 'var(--spacing-6)' }}>
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: isYearly ? 'normal' : 'var(--font-semibold)', color: isYearly ? 'var(--color-gray-500)' : 'var(--color-gray-900)' }}>
              Monthly
            </span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              style={{
                width: '56px',
                height: '28px',
                borderRadius: '14px',
                background: isYearly ? 'var(--color-primary)' : 'var(--color-gray-300)',
                border: 'none',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background 0.2s'
              }}
            >
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: 'white',
                position: 'absolute',
                top: '2px',
                left: isYearly ? '30px' : '2px',
                transition: 'left 0.2s'
              }} />
            </button>
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: isYearly ? 'var(--font-semibold)' : 'normal', color: isYearly ? 'var(--color-gray-900)' : 'var(--color-gray-500)' }}>
              Yearly
              <span style={{ marginLeft: 'var(--spacing-2)', color: 'var(--color-success)', fontWeight: 'var(--font-bold)' }}>
                (Save 10%)
              </span>
            </span>
          </div>
        </header>

        <main className="pricing-content">
          <div className="pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--spacing-6)', maxWidth: '1200px', margin: '0 auto', padding: 'var(--spacing-6)' }}>
            {SUBSCRIPTION_PLANS.map((plan) => (
              <Card key={plan.name} className={`pricing-card ${plan.popular ? 'popular' : ''}`} style={{ position: 'relative', border: plan.popular ? '2px solid var(--color-primary)' : '1px solid var(--color-gray-200)' }}>
                {plan.popular && (
                  <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)' }}>
                    <Badge variant="primary">Most Popular</Badge>
                  </div>
                )}
                <CardBody>
                  <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-4)' }}>
                    <h3 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-gray-900)', marginBottom: 'var(--spacing-2)' }}>
                      {plan.name}
                    </h3>
                    
                    {/* Credits Badge */}
                    <div style={{ display: 'inline-block', background: 'var(--color-primary-light, #e0f2fe)', color: 'var(--color-primary)', padding: 'var(--spacing-2) var(--spacing-4)', borderRadius: 'var(--radius-full)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-3)' }}>
                      {plan.creditsPerMonth} credits/month
                    </div>
                    
                    <div style={{ marginBottom: 'var(--spacing-4)' }}>
                      <div style={{ fontSize: 'var(--text-4xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-primary)' }}>
                        ${getMonthlyEquivalent(plan)}
                      </div>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
                        /month {isYearly && '(billed yearly)'}
                      </div>
                      {isYearly && (
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-success)', marginTop: 'var(--spacing-1)' }}>
                          ${plan.yearlyPrice}/year - Save ${(plan.monthlyPrice * 12 - plan.yearlyPrice).toFixed(0)}!
                        </div>
                      )}
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', marginTop: 'var(--spacing-1)' }}>
                        ${(parseFloat(String(getMonthlyEquivalent(plan))) / plan.creditsPerMonth).toFixed(2)} per credit
                      </div>
                    </div>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', fontStyle: 'italic', minHeight: '40px' }}>
                      {plan.description}
                    </p>
                  </div>

                  <div style={{ marginBottom: 'var(--spacing-6)' }}>
                    {plan.features.map((feature, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}>
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)' }}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', textAlign: 'center', marginBottom: 'var(--spacing-4)' }}>
                    {plan.description.includes('simple') && 'Get started with our essential tools for accurate measurements and basic adjustments.'}
                    {plan.description.includes('scale') && 'Perfect for growing contractors who need comprehensive tools and advanced features.'}
                    {plan.description.includes('ultimate') && 'Our most comprehensive package for established businesses requiring full automation.'}
                  </p>

                  <Button
                    variant={plan.popular ? 'primary' : 'outline'}
                    fullWidth
                    onClick={() => handleSelectPlan(plan)}
                  >
                    {plan.cta}
                  </Button>
                </CardBody>
              </Card>
            ))}
          </div>
        </main>

        {/* Payment Modal */}
        <Modal
          isOpen={!!selectedPlan}
          onClose={() => setSelectedPlan(null)}
          title={`Subscribe to ${selectedPlan?.name} Plan`}
        >
          {selectedPlan && (
            <SubscriptionPaymentForm
              planName={selectedPlan.name}
              billingCycle={isYearly ? 'yearly' : 'monthly'}
              amount={getPrice(selectedPlan)}
              creditsPerMonth={selectedPlan.creditsPerMonth}
              onSuccess={handlePaymentSuccess}
              onCancel={() => setSelectedPlan(null)}
            />
          )}
        </Modal>
      </div>
    </Layout>
  );
};

export default Pricing;
